import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * Detects when a table header scrolls out of the viewport and provides
 * state for rendering a fixed clone. Syncs horizontal scroll position
 * so the clone tracks the real table via an imperative callback.
 */
export function useFixedTableHeader(
    theadRef: RefObject<HTMLTableSectionElement | null>,
    scrollRef: RefObject<HTMLDivElement | null>,
    wrapperRef: RefObject<HTMLDivElement | null>,
    onScrollSync?: (left: number) => void
) {
    const [isFixed, setIsFixed] = useState(false)
    const [portalLeft, setPortalLeft] = useState(0)
    const [portalRight, setPortalRight] = useState(0)
    const rafId = useRef(0)

    // Track when any part of the thead is clipped by the viewport top.
    // The content area scrolls inside a layout container (CoreLayout's
    // overflow-auto div), not the window, so we find and listen on that.
    useEffect(() => {
        const thead = theadRef.current
        const wrapper = wrapperRef.current
        if (!thead || !wrapper) return

        // Walk up to the nearest scrollable ancestor
        let scrollParent: HTMLElement | Window = window
        let el: HTMLElement | null = wrapper.parentElement
        while (el) {
            const { overflowY } = getComputedStyle(el)
            if (overflowY === 'auto' || overflowY === 'scroll') {
                scrollParent = el
                break
            }
            el = el.parentElement
        }

        const update = () => {
            const theadRect = thead.getBoundingClientRect()
            const wrapperRect = wrapper.getBoundingClientRect()
            const parentTop = scrollParent instanceof Window ? 0 : scrollParent.getBoundingClientRect().top
            const headerClipped = theadRect.top < parentTop
            const tableStillVisible = wrapperRect.bottom > (parentTop + theadRect.height)
            setIsFixed(headerClipped && tableStillVisible)
            setPortalLeft(wrapperRect.left)
            setPortalRight(window.innerWidth - wrapperRect.right)
        }

        const observer = new IntersectionObserver(update, {
            threshold: [0, 1]
        })
        observer.observe(thead)

        scrollParent.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update, { passive: true })
        return () => {
            observer.disconnect()
            scrollParent.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [theadRef, wrapperRef])

    // Sync horizontal scroll via imperative callback
    useEffect(() => {
        const scrollEl = scrollRef.current
        if (!scrollEl || !onScrollSync) return

        const onScroll = () => {
            cancelAnimationFrame(rafId.current)
            rafId.current = requestAnimationFrame(() => {
                onScrollSync(scrollEl.scrollLeft)
            })
        }

        scrollEl.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            scrollEl.removeEventListener('scroll', onScroll)
            cancelAnimationFrame(rafId.current)
        }
    }, [scrollRef, onScrollSync])

    return { isFixed, portalLeft, portalRight }
}
